<template>
  <WizardContainer
    width="792px"
    height="624px"
  >
    <WizardContent
      class="demo-provide-email-wizard-content"
    >
      <template #flashMessage>
        <EduWarningFlashMessage :email="email" />
      </template>
      <h4 class="mt-6 mb-8">
        Provide Your Work Email
      </h4>
      <p class="p3 mb-9">
        JobNav accounts require a work email address.
      </p>
      <v-form
        ref="form"
        v-model="valid"
        class="form"
        @submit.prevent="submitForm"
      >
        <DpplTextField
          v-model="email"
          class="mb-3"
          label="Company Email"
          :rules="emailRules"
          validate-on="blur"
        />
        <DpplSignupFormButton
          type="submit"
          block
          :loading="loading"
          class="mb-4"
        >
          Continue
        </DpplSignupFormButton>
        <WizardFooter>
          <h5>
            Already have an account?
            <router-link
              class="pl-2"
              to="login"
            >
              Sign In
            </router-link>
          </h5>
        </WizardFooter>
      </v-form>
    </WizardContent>
  </WizardContainer>
</template>

<script>
import WizardContainer from '@/components/shared/WizardContainer.vue';
import WizardContent from '@/components/shared/WizardContent.vue';
import WizardFooter from '@/components/shared/WizardFooter.vue';
import { signupCreateLeadUserDemo } from '@/api/signup';
import validation from '@/helpers/validation';
import DpplTextField from '@/components/datapeople/DpplTextField.vue';
import DpplSignupFormButton from '@/components/datapeople/DpplSignupFormButton.vue';
import EduWarningFlashMessage from '@/components/shared/EduWarningFlashMessage.vue';
import { mapActions } from 'pinia';
import { useWizard } from '@/stores/wizard/wizard';

export default {
  name: 'ProvideWorkEmail',
  components: {
    WizardContainer,
    WizardContent,
    WizardFooter,
    DpplTextField,
    DpplSignupFormButton,
    EduWarningFlashMessage,
  },
  emits: ['track-page-load'],
  data() {
    return {
      firstName: this.$route.params.firstName,
      lastName: this.$route.params.lastName,
      email: '',
      emailRules: [
        (v) => !validation.isEmpty(v) || 'Email is required',
        (v) => validation.emailFormat(v) || 'Email format is invalid',
        (v) => validation.emailBlacklist(v) || 'Please provide your work email address.',
        (v) => validation.roleEmailBlacklist(v) || 'Role-based email addresses are not valid. Please use your individual company email address.',
        (v) => (v && v.length <= 250) || 'Email is too long',
      ],
      valid: false,
      loading: false,
    };
  },
  created() {
    if (!this.firstName || !this.lastName) {
      this.$router.push({
        name: 'demo-request',
      });
    }
  },
  beforeCreate() {
    this.$emit('track-page-load', { page: 'Demo Provide Your Work Email' });
  },
  methods: {
    ...mapActions(useWizard, ['updateFlash']),
    async submitForm() {
      try {
        const formValidateResult = await this.$refs.form.validate();
        if (formValidateResult.valid) {
          this.loading = true;
          const responseJSON = await signupCreateLeadUserDemo({
            firstName: this.firstName,
            lastName: this.lastName,
            email: this.email,
          });
          if (responseJSON.success === true) {
            if (responseJSON.has_company_info === true) {
              this.$router.push({
                name: 'demo-request-received',
                params: {
                  firstName: this.firstName,
                },
              });
            } else {
              this.$router.push({
                name: 'demo-about-your-company',
                params: {
                  firstName: this.firstName,
                  projectId: responseJSON.project_id,
                },
              });
            }
          }
        }
      } catch (e) {
        if (!e.message || e.message.charAt(0) !== '{') {
          this.updateFlash({ message: `Create lead user error: ${e.message}` });
          throw e;
        }
        const responseJSON = JSON.parse(e.message);
        // if response contains please use corporate email error and no errors in other fields
        if ( // If only error is please include corporate email
          responseJSON.errors
          && responseJSON.errors.email
          && responseJSON.errors.email.includes('Please use your corporate email address.')
        ) {
          this.updateFlash({ message: 'Please use your corporate email address' });
        } else if (
          responseJSON.errors
          && Object.keys(responseJSON.errors).length === 1
          && responseJSON.errors.auth
          && responseJSON.errors.auth.includes('user already active.')
        ) {
          this.updateFlash({ template: 'user-active' });
        } else {
          this.updateFlash({ message: 'Something went wrong' });
        }
        throw e;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="less" scoped>
.form {
  width: 100%;
}
</style>
